// Components/Reservations/ReservationModal.jsx
import { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { ReservationsFetchAPI } from "../../api/Reservations";

/* ── ICONS ── */
const IcoCalendar = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="8" y1="14" x2="8.01" y2="14" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="12" y1="14" x2="12.01" y2="14" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="16" y1="14" x2="16.01" y2="14" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);
const IcoX = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IcoCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IcoAlert = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const IcoUsers = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/* ─────────────────────────────────────────────────────────
   COMPONENT — lógica 100% original, solo visual nuevo
───────────────────────────────────────────────────────── */
export default function ReservationModal({ show, onHide, businessCode, branchofficeCode, clientData, token }) {

  /* ── LÓGICA ORIGINAL INTACTA ── */
  const [form, setForm] = useState({
    name: clientData && clientData.name ? clientData.name : "",
    phone: clientData && clientData.phone ? clientData.phone : "",
    email: clientData && clientData.email ? clientData.email : "",
    date: moment().format("YYYY-MM-DD"),
    time: "19:00",
    people: 2,
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const isGuest = useMemo(() => !clientData || !clientData.id, [clientData]);

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      setMsg({ type: "", text: "" });

      const reserved_at = moment(`${form.date} ${form.time}`, "YYYY-MM-DD HH:mm").format("YYYY-MM-DD HH:mm:ss");

      const payload = {
        business_code: businessCode,
        branchoffice_code: branchofficeCode,
        client_id: !isGuest ? clientData.id : null,
        guest_name: form.name,
        guest_phone: form.phone,
        guest_email: form.email,
        reserved_at,
        people_qty: Number(form.people),
        notes: form.notes,
      };

      const resp = await ReservationsFetchAPI.createReservation(payload, token);
      if (!resp.data.success) {
        setMsg({ type: "danger", text: resp.data.error || "Reserva no pudo ser creada" });
        return;
      }

      setMsg({ type: "success", text: "¡Reserva solicitada! Te contactaremos para confirmarla." });
      setTimeout(() => onHide(), 1000);
    } catch (err) {
      console.error(err);
      setMsg({ type: "danger", text: "Unexpected error" });
    } finally {
      setLoading(false);
    }
  };
  /* ── FIN LÓGICA ORIGINAL ── */


  /* ESC to close */
  useEffect(() => {
    if (!show) return;
    const fn = (e) => { if (e.key === "Escape") onHide(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [show, onHide]);

  /* body scroll lock */
  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  if (!show) return null;

  /* ── RENDER ── */
  return createPortal(
    <div className="rm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onHide(); }}>
      <div className="rm-panel" role="dialog" aria-modal="true" aria-label="Realizar Reserva">

        {/* HEADER */}
        <div className="rm-header">
          <div className="rm-header-icon"><IcoCalendar /></div>
          <div className="rm-header-title">
            Realizar Reserva
            <span className="rm-header-sub">Completa los datos y te confirmamos</span>
          </div>
          <button className="rm-close" onClick={onHide} aria-label="Cerrar" disabled={loading}>
            <IcoX />
          </button>
        </div>

        {/* BODY */}
        <div className="rm-body">

          {/* alert */}
          {msg.text && (
            <div className={`rm-alert rm-alert-${msg.type}`}>
              <IcoAlert />
              {msg.text}
            </div>
          )}

          {/* nombre + teléfono */}
          <div className="rm-row">
            <div className="rm-field">
              <label className="rm-label">Nombre</label>
              <input
                className="rm-input"
                value={form.name}
                onChange={(e) => onChange("name", e.target.value)}
                placeholder="Tu nombre"
              />
            </div>
            <div className="rm-field">
              <label className="rm-label">Teléfono</label>
              <input
                className="rm-input"
                value={form.phone}
                onChange={(e) => onChange("phone", e.target.value)}
                placeholder="Ej: 6xxx-xxxx"
              />
            </div>
          </div>

          {/* correo + personas */}
          <div className="rm-row">
            <div className="rm-field">
              <label className="rm-label">Correo</label>
              <input
                className="rm-input"
                type="email"
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div className="rm-field">
              <label className="rm-label">
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}><IcoUsers />Personas</span>
              </label>
              <div className="rm-stepper">
                <button
                  type="button"
                  className="rm-stepper-btn"
                  onClick={() => onChange("people", Math.max(1, Number(form.people) - 1))}
                  disabled={loading}
                >−</button>
                <span className="rm-stepper-val">{form.people}</span>
                <button
                  type="button"
                  className="rm-stepper-btn"
                  onClick={() => onChange("people", Math.min(20, Number(form.people) + 1))}
                  disabled={loading}
                >+</button>
              </div>
            </div>
          </div>

          {/* fecha + hora */}
          <div className="rm-row">
            <div className="rm-field">
              <label className="rm-label">Fecha</label>
              <input
                className="rm-input"
                type="date"
                value={form.date}
                onChange={(e) => onChange("date", e.target.value)}
              />
            </div>
            <div className="rm-field">
              <label className="rm-label">Hora</label>
              <input
                className="rm-input"
                type="time"
                value={form.time}
                onChange={(e) => onChange("time", e.target.value)}
              />
            </div>
          </div>

          {/* notas */}
          <div className="rm-row rm-full">
            <div className="rm-field">
              <label className="rm-label">Notas <span style={{ fontWeight: 400, color: "var(--text-3)" }}>— opcional</span></label>
              <textarea
                className="rm-textarea"
                rows={2}
                value={form.notes}
                onChange={(e) => onChange("notes", e.target.value)}
                placeholder="Ej: mesa cerca de ventana, cumpleaños, etc."
              />
            </div>
          </div>

        </div>

        {/* FOOTER */}
        <div className="rm-footer">
          <button className="rm-btn-cancel" onClick={onHide} disabled={loading}>
            Cancelar
          </button>
          <button className="rm-btn-submit" onClick={onSubmit} disabled={loading}>
            {loading
              ? <><span className="rm-spinner" /> Enviando...</>
              : <><IcoCheck /> Solicitar reserva</>}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}